import { useOutletContext } from 'react-router-dom';
import type { ApiClient } from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import { LoadingState } from '../muster';
import { AussparungAbgleich } from './AussparungAbgleich';
import { AussparungArbeit } from './AussparungArbeit';
import { AussparungBaum } from './AussparungBaum';

/**
 * Aussparungen > Abgleich / Arbeitsvorrat / Baum — dünne Routenhüllen.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM EINE EIGENE DATEI UND NICHT DREI ROUTEN AUF DIE BESTEHENDEN DATEIEN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `AussparungAbgleich`, `AussparungArbeit` und `AussparungBaum` waren bis
 * N3 NUR über den internen Reiterzustand von `Aussparungen.tsx` erreichbar
 * (Plan Abschnitt 3.6: „ohne Route"). Alle drei nehmen `api`/`projektId`
 * bereits als Props entgegen und tragen ihre eigene Kopfzeile
 * (`ContentSection`, kein `PageHeader`) — sie lassen sich DESHALB, anders
 * als z. B. die Kueche-Tabs in `Kuechenplanung.tsx` (dort teilen sich fünf
 * Ansichten einen gemeinsamen `PageHeader` und dieselbe `Kennzahlen`-Zeile
 * einer einzigen Datenabfrage), ohne Umbau ihrer selbst routen: Diese Datei
 * ergänzt nur den `PageHeader`, den eine eigenständige Seite braucht, und
 * verweist auf `projekt.id` aus dem Rahmen-Kontext.
 *
 * Der bestehende Reiterwechsel INNERHALB von `Aussparungen.tsx` bleibt
 * unverändert stehen (kein bestehender Pfad ändert sich) — diese drei
 * Routen sind ein zusätzlicher, direkt verlinkbarer Zugang zu denselben
 * Komponenten, keine Ablösung.
 */

interface Rahmen { api: ApiClient; projekt?: { id: string } }

export function AussparungAbgleichSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState text="Projekt wird geladen …" zeilen={6} />;

  return (
    <>
      <PageHeader
        titel="Abgleich"
        beschreibung={'Derselbe Durchbruch steht in zwei Modellen: in conVoid mit dem '
          + 'Freigabestand, im ARDU-Modell mit der Anmerkung des Architekten.'}
      />
      <AussparungAbgleich api={api} projektId={projekt.id} />
    </>
  );
}

export function AussparungArbeitSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState text="Projekt wird geladen …" zeilen={6} />;

  return (
    <>
      <PageHeader
        titel="Arbeitsvorrat"
        beschreibung={'Die Aussparungen nach Geschoss und Planungsbereich sowie die '
          + 'offenen Anforderungen — die Sicht, in der abschnittsweise gearbeitet wird.'}
      />
      <AussparungArbeit api={api} projektId={projekt.id} />
    </>
  );
}

export function AussparungBaumSeite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState text="Projekt wird geladen …" zeilen={6} />;

  return (
    <>
      <PageHeader
        titel="Baum"
        beschreibung="Geschoss → Bauart → Aussparung → Medien."
      />
      <AussparungBaum api={api} projektId={projekt.id} />
    </>
  );
}
